import { useEffect, useMemo, useRef, useState } from "react";
import { Dialog, useDialogRequestClose } from "@/components/ui/Dialog";
import { hasFormChanges } from "@/components/ui/dialogCloseGuard";
import { TextInput } from "@/components/ui/Field";
import {
  TimesheetFlowFooterButton,
} from "@/features/timesheetFlow/components/TimesheetFlowFooter";
import { LockedAutocomplete } from "@/components/LockedAutocomplete";
import type { AutocompleteOption, LockedAutocompleteHandle } from "@/components/LockedAutocomplete";
import { api } from "@/lib/apiClient";
import type { TimesheetRowRecord } from "@/features/timesheetFlow/timesheetFlowTypes";
import {
  computeLiveDescription,
  finalizeTimesheetDescription,
  validateTimeRowForm,
  type TimeRowFormState,
} from "@/features/timesheetFlow/lib/timesheetTimeRowUtils";
import { useTimesheetFlowLayers } from "@/features/timesheetFlow/timesheetFlowLayers";

export interface TimesheetEmployee {
  display_name: string;
  account_type?: string | null;
  user_id?: number;
}

let cachedEmployees: TimesheetEmployee[] | null = null;

export async function loadTimesheetEmployees(): Promise<TimesheetEmployee[]> {
  if (cachedEmployees) return cachedEmployees;
  const data = await api
    .get<Array<{ display_name?: string; name?: string; user_id?: number; account_type?: string | null }>>(
      "/hero/employees",
    )
    .catch(() => []);
  cachedEmployees = (Array.isArray(data) ? data : [])
    .map((e) => ({
      display_name: String(e.display_name || e.name || "").trim(),
      account_type: e.account_type ?? null,
      user_id: e.user_id,
    }))
    .filter((e) => e.display_name);
  return cachedEmployees;
}

export async function timesheetEmployeeSource(
  query: string,
): Promise<AutocompleteOption<TimesheetEmployee>[]> {
  const list = await loadTimesheetEmployees();
  const q = query.trim().toLowerCase();
  const filtered = q ? list.filter((e) => e.display_name.toLowerCase().includes(q)) : list;
  return filtered.slice(0, 50).map((e) => ({
    key: String(e.user_id ?? e.display_name),
    label: e.display_name,
    value: e,
  }));
}

function employeeToOption(employee: TimesheetEmployee): AutocompleteOption<TimesheetEmployee> {
  return {
    key: String(employee.user_id ?? employee.display_name),
    label: employee.display_name,
    value: employee,
  };
}

function findEmployeeOption(
  name: string,
  employees: TimesheetEmployee[],
): AutocompleteOption<TimesheetEmployee> | null {
  const trimmed = name.trim();
  if (!trimmed) return null;
  const match = employees.find((e) => e.display_name === trimmed);
  if (match) return employeeToOption(match);
  return {
    key: `manual:${trimmed}`,
    label: trimmed,
    value: { display_name: trimmed, account_type: null },
  };
}

function rowToForm(row: TimesheetRowRecord | null | undefined): TimeRowFormState {
  if (!row) {
    return {
      person_name: "",
      work_date: "",
      time_from: "",
      time_to: "",
      hours: "",
      comment: "Arbeitszeit",
    };
  }
  return {
    person_name: row.person_name || "",
    work_date: row.work_date || "",
    time_from: row.time_from || "",
    time_to: row.time_to || "",
    hours:
      row.hours_decimal != null
        ? String(row.hours_decimal).replace(".", ",")
        : "",
    comment: row.comment || "",
  };
}

export function TimesheetTimeRowDialog({
  open,
  editRow,
  showTimeRange,
  onClose,
  onSave,
}: {
  open: boolean;
  editRow: TimesheetRowRecord | null;
  showTimeRange: boolean;
  onClose: () => void;
  onSave: (row: TimesheetRowRecord, mode: "create" | "edit") => void;
}) {
  const layers = useTimesheetFlowLayers();
  const mode = editRow ? "edit" : "create";
  const [form, setForm] = useState<TimeRowFormState>(() => rowToForm(editRow));
  const [baseline, setBaseline] = useState<TimeRowFormState>(() => rowToForm(editRow));
  const [employeeOption, setEmployeeOption] = useState<AutocompleteOption<TimesheetEmployee> | null>(null);
  const [baselineEmployee, setBaselineEmployee] = useState("");
  const [error, setError] = useState<string | null>(null);
  const employeeAcRef = useRef<LockedAutocompleteHandle>(null);

  const createManualEmployeeOption = (name: string): AutocompleteOption<TimesheetEmployee> => ({
    key: `manual:${name}`,
    label: name,
    value: { display_name: name, account_type: null },
  });

  const resolveEmployee = () => {
    const pending = employeeAcRef.current?.getPendingQuery()?.trim() ?? "";
    if (employeeOption?.label?.trim()) {
      return {
        name: employeeOption.label.trim(),
        accountType: employeeOption.value?.account_type ?? null,
      };
    }
    if (pending) {
      return { name: pending, accountType: null };
    }
    return { name: form.person_name.trim(), accountType: null };
  };

  useEffect(() => {
    if (!open) return;
    const nextForm = rowToForm(editRow);
    setForm(nextForm);
    setBaseline(nextForm);
    setBaselineEmployee(nextForm.person_name);
    setError(null);
    void loadTimesheetEmployees().then((employees) => {
      setEmployeeOption(findEmployeeOption(nextForm.person_name, employees));
    });
  }, [open, editRow]);

  const dirty = useMemo(() => {
    const employee = employeeOption?.label?.trim() ?? form.person_name.trim();
    return hasFormChanges(form, baseline) || employee !== baselineEmployee.trim();
  }, [form, baseline, employeeOption, baselineEmployee]);

  const patch = (p: Partial<TimeRowFormState>) => setForm((prev) => ({ ...prev, ...p }));

  const handleEmployeeChange = (opt: AutocompleteOption<TimesheetEmployee> | null) => {
    setEmployeeOption(opt);
    const employee = opt?.label?.trim() ?? "";
    const accountType = opt?.value?.account_type;
    setForm((prev) => ({
      ...prev,
      person_name: employee,
      comment: computeLiveDescription(prev.comment, prev.work_date, employee, accountType),
    }));
  };

  const handleDateChange = (workDate: string) => {
    setForm((prev) => ({
      ...prev,
      work_date: workDate,
      comment: computeLiveDescription(
        prev.comment,
        workDate,
        prev.person_name.trim(),
        employeeOption?.value?.account_type,
      ),
    }));
  };

  const handleSubmit = () => {
    const { name: employee, accountType } = resolveEmployee();
    const submitForm: TimeRowFormState = { ...form, person_name: employee };
    const result = validateTimeRowForm(submitForm, showTimeRange);
    if (!result.ok) {
      setError(result.error);
      return;
    }

    const comment = finalizeTimesheetDescription(
      submitForm.comment,
      submitForm.work_date,
      employee,
      accountType,
    );

    const row: TimesheetRowRecord = {
      ...(editRow ?? {}),
      person_name: employee,
      work_date: submitForm.work_date,
      hours_decimal: result.hoursDecimal,
      time_from: showTimeRange && submitForm.time_from ? submitForm.time_from : null,
      time_to: showTimeRange && submitForm.time_to ? submitForm.time_to : null,
      comment,
    };

    onSave(row, mode);
    onClose();
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      dirty={dirty}
      title={mode === "edit" ? "Zeitzeile bearbeiten" : "Zeitzeile hinzufügen"}
      size="md"
      zIndexClass={layers.overlay}
      fullScreenMobile
      bodyClassName="timesheet-flow-shell"
      footer={
        <TimeRowDialogFooter onClose={onClose} onSubmit={handleSubmit} />
      }
    >
      <div className="space-y-3">
        <LockedAutocomplete<TimesheetEmployee>
          ref={employeeAcRef}
          label="Mitarbeiter"
          placeholder="Mitarbeiter suchen oder eingeben …"
          value={employeeOption}
          minChars={0}
          allowFreeText
          createFreeTextOption={createManualEmployeeOption}
          loadOptions={timesheetEmployeeSource}
          onChange={handleEmployeeChange}
        />
        <TextInput
          label="Datum"
          type="date"
          max={today}
          value={form.work_date}
          onChange={(e) => handleDateChange(e.target.value)}
        />
        {showTimeRange ? (
          <div className="grid grid-cols-2 gap-3">
            <TextInput
              label="Von"
              type="time"
              value={form.time_from}
              onChange={(e) => patch({ time_from: e.target.value })}
            />
            <TextInput
              label="Bis"
              type="time"
              value={form.time_to}
              onChange={(e) => patch({ time_to: e.target.value })}
            />
          </div>
        ) : null}
        <TextInput
          label="Stunden"
          value={form.hours}
          placeholder="0,00"
          inputMode="decimal"
          className="text-end"
          onChange={(e) => patch({ hours: e.target.value })}
        />
        <TextInput
          label="Beschreibung"
          value={form.comment}
          placeholder="Tätigkeit"
          onChange={(e) => patch({ comment: e.target.value })}
        />
        {error ? <p className="text-sm text-status-red">{error}</p> : null}
      </div>
    </Dialog>
  );
}

function TimeRowDialogFooter({
  onClose,
  onSubmit,
}: {
  onClose: () => void;
  onSubmit: () => void;
}) {
  const requestClose = useDialogRequestClose(onClose);
  return (
    <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
      <TimesheetFlowFooterButton type="button" variant="secondary" onClick={requestClose}>
        Abbrechen
      </TimesheetFlowFooterButton>
      <TimesheetFlowFooterButton type="button" onClick={onSubmit}>
        Übernehmen
      </TimesheetFlowFooterButton>
    </div>
  );
}
